import { useCallback, useRef } from 'react';
import { useTranslation, type WebsiteInterfaceLocale } from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared/i18n';

/** A translate function that takes keys built at run time (server title keys, region label ids). */
export type WebsiteT = (key: string, options?: Record<string, unknown>) => string;

export interface UseWebsiteT {
  readonly t: WebsiteT;
  readonly dir: 'rtl' | 'ltr';
  readonly lang: WebsiteInterfaceLocale;
}

/**
 * The editor builds many keys at run time: `admin.websiteTemplates.catalog.<slug>.title`
 * comes from the server, `region.<labelId>.edit` from the manifest. This hook
 * gives a `t` that accepts them. `t` is stable for as long as the language does
 * not change, so effects and callbacks can list it as a dependency.
 */
export function useWebsiteT(): UseWebsiteT {
  const { t, dir, lang } = useTranslation();
  const latest = useRef(t);
  latest.current = t;
  const stable = useCallback<WebsiteT>(
    (key, options) => latest.current(key as TranslationKey, options),
    // The identity of `t` follows the language, nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang],
  );
  return { t: stable, dir, lang };
}
